// src/pages/seller/SellerDashboardLayout.jsx
import React, { useEffect } from 'react';
import { 
  BarChart3, Package, ShoppingBag, 
  Truck, CreditCard, User, 
  FileText, Home, MessageCircle, Star 
} from 'lucide-react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useApp } from '../../context/AppContext';

const sidebarItems = [
  { path: '', label: 'Overview', icon: BarChart3 },
  { path: 'products', label: 'Product Management', icon: Package },
  { path: 'orders', label: 'Order Management', icon: ShoppingBag },
  { path: 'shipping', label: 'Logistics / Shipping', icon: Truck },
  { path: 'financials', label: 'Financials', icon: CreditCard },
  { path: 'profile', label: 'Seller Profile', icon: User },
  { path: 'analytics', label: 'Analytics', icon: FileText },
  { path: 'messages', label: 'Inbox', icon: MessageCircle },
  { path: 'reviews', label: 'Customer Reviews', icon: Star }
];

const SellerDashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthReady } = useApp();

  useEffect(() => {
    if (isAuthReady) {
      if (!user?.uid) {
        console.log('SellerDashboardLayout - No user, redirecting to home');
        navigate('/');
        return;
      }
      if (user.userType !== 'seller') {
        console.log('SellerDashboardLayout - User is not seller, redirecting to home');
        navigate('/');
        return;
      }
    }
  }, [user, isAuthReady, navigate]);

  const isActive = (path) => {
    if (path === '') return location.pathname === '/seller';
    return location.pathname === `/seller/${path}`;
  };

  // 인증이 완료되지 않았거나 사용자가 seller가 아닌 경우 로딩 표시
  if (!isAuthReady || !user?.uid || user.userType !== 'seller') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="flex gap-8">
        {/* Sidebar */}
        <div className="w-64 flex-shrink-0">
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-700 hover:text-gray-900 mb-6"
            >
              <Home size={16} />
              <span className="font-medium">Back to Home</span>
            </button>
            <nav className="space-y-1">
              {sidebarItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.path || 'overview'}
                    onClick={() => navigate(`/seller/${item.path}`)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                      isActive(item.path) 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={18} />
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerDashboardLayout;